import {Component, HostListener, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {TeacherSessionApiService} from './teacher-session-api.service';
import {DebriefChartComponent} from './debrief-chart.component';
import {DebriefChart, DebriefSeries, SebgDebriefing, SebgPeriod, SebgTeam, SebgTeamPeriod, SebgChoice, PositionPoint} from './sebg-debriefing.models';

const scoreDimensions: [string, [string, number][]][] = [
  ['Economic', [['norm_roi', 2], ['norm_roe', 1], ['norm_id', 1], ['norm_qr', 1]]],
  ['Environmental', [['norm_vamp', 1], ['norm_vaen', 1], ['norm_vase', 1]]],
  ['Social', [['norm_vsi', 1], ['norm_vse', 1]]]
];
const slides = [
  {id: 0, title: 'Session ranking', section: 'Overview', prompt: 'How does current performance differ from your ranking?'},
  {id: 13, title: 'Company positioning', section: 'Overview', prompt: 'How has your company balanced economic performance with environmental and social value?'},
  {id: 1, title: 'Score over time', section: 'Overview', prompt: 'How does current performance differ from your ranking?'},
  {id: 2, title: 'Economic, Environmental and Social Value', section: 'Overview', prompt: 'Which dimension explains the differences between teams?'},
  {id: 3, title: 'ROI', section: 'Financial performance', prompt: 'How effectively are assets generating operating profit?'},
  {id: 4, title: 'ROE', section: 'Financial performance', prompt: 'What explains the difference between ROI and ROE?'},
  {id: 5, title: 'Debt ratio', section: 'Financial performance', prompt: 'How has financing supported your strategy?'},
  {id: 6, title: 'Quick ratio', section: 'Financial performance', prompt: 'How has short-term financial balance changed?'},
  {id: 7, title: 'Environmental profile', section: 'Sustainability and business evolution', prompt: 'Where did your environmental choices make a difference?'},
  {id: 8, title: 'Social profile', section: 'Sustainability and business evolution', prompt: 'How did you balance internal and external social priorities?'},
  {id: 9, title: 'Traditional and Sustainable Sales', section: 'Sustainability and business evolution', prompt: 'When did the sustainable line become important to your business?'},
  {id: 10, title: 'Price positioning', section: 'Sustainability and business evolution', prompt: 'How did your price positioning change relative to competitors?'},
  {id: 11, title: 'Inventory and production', section: 'Sustainability and business evolution', prompt: 'How well did production match sales over time?'},
  {id: 12, title: 'Decision changes over time', section: 'Decisions', prompt: 'Which changes reflect a deliberate adjustment of strategy?'}
];

@Component({
  selector: 'app-sebg-debriefing', standalone: true,
  imports: [CommonModule, FormsModule, DebriefChartComponent],
  templateUrl: './sebg-debriefing.component.html', styleUrls: ['./sebg-debriefing.component.scss']
})
export class SebgDebriefingComponent implements OnInit {
  data?: SebgDebriefing;
  loading = false;
  error = '';
  slide = 0;
  periodNumber: number | null = null;
  teamIds: string[] = [];
  scoreMetric = 'score';
  salesView = 'comparison';
  line = 't';
  focusTeam = '';
  detailTeam = '';
  get chartId(): number { return this.activeSlide.id; }
  get detailDecisions() { return this.teamPeriod(this.period, this.detailTeam)?.decisions ?? []; }
  get initialDecisions() { return this.detailDecisions.filter(c => c.initial); }
  get recurringDecisions() { return this.detailDecisions.filter(c => !c.initial); }
  get positionRows() { return this.chart?.series.flatMap(s => (s.points ?? []).filter(p => p != null).map(p => ({team:s.label, ...p!}))) ?? []; }
  menuOpen = false;
  revealed = 1;
  chart?: DebriefChart;
  slides = slides;
  sections = [...new Set(slides.map(s => s.section))];
  conditionalPrompts: string[] = [];
  constructor(private api: TeacherSessionApiService) {}
  trackTeam(_index: number, team: SebgTeam): string { return team.id; }
  ngOnInit(): void { this.refresh(); }
  get activeSlide() { return slides[this.slide]; }
  get period(): SebgPeriod | undefined { return this.data?.periods.find(p => p.number === this.periodNumber); }
  get history(): SebgPeriod[] { return this.data?.periods.filter(p => this.periodNumber != null && p.number <= this.periodNumber) ?? []; }
  get teams(): SebgTeam[] { return this.data?.teams ?? []; }
  get visibleRanking() { return this.period?.ranking.slice(-this.revealed) ?? []; }
  get hasValues(): boolean { return !!this.chart?.series.some(s => (s.points ? s.points.some(v => v != null) : s.values.some(v => v != null))); }
  get hasMissing(): boolean { return !!this.chart?.series.some(s => (s.points ? s.points.some(v => v == null) : s.values.some(v => v == null))); }
  get availabilityIssues(): {team: string; reasons: string[]}[] {
    const availability = this.period?.charts.find(c => c.chartId === `CH-${String(this.chartId).padStart(2,'0')}`);
    return this.teams.map(t => ({team: t.name, reasons: availability?.missingByTeam[t.id] ?? []})).filter(t => t.reasons.length > 0);
  }
  get scoreMismatch(): boolean {
    return this.chartId === 2 && this.availabilityIssues.some(t => t.reasons.some(r => r.includes('do not reconcile')));
  }
  indicatorLabel(key: string): string {
    const labels: Record<string,string> = {norm_roi:'ROI contribution (before weighting)', norm_roe:'ROE contribution',
      norm_id:'Debt ratio contribution',norm_qr:'Quick ratio contribution',norm_vamp:'Raw materials score',norm_vaen:'Energy score',
      norm_vase:'Waste / emissions score',norm_vsi:'Internal social score',norm_vse:'External social score',score_this:'Stored decision score',
      i_roi:'ROI (%)',i_roe:'ROE (%)',i_id:'Debt / equity × 100',i_qr:'Quick ratio × 100',sp_PN:'Equity (game currency)',
      punti_vamp:'Raw materials model points',punti_vaen:'Energy model points',punti_vase:'Waste / emissions model points',
      punti_vsi:'Internal social model points',punti_vse:'External social model points'};
    if (labels[key]) return labels[key];
    if (key.startsWith('offer_')) return 'Persisted offer (units)';
    if (key.startsWith('production_')) return 'Derived production (units)';
    if (key.startsWith('sales_number_')) return 'Sales (units)';
    if (key.startsWith('magazzino_')) return 'Closing inventory (units)';
    return key;
  }
  get relevantKeys(): string[] {
    return ({2: ['norm_roi','norm_roe','norm_id','norm_qr','norm_vamp','norm_vaen','norm_vase','norm_vsi','norm_vse','score_this'], 3: ['i_roi'], 4: ['i_roe','sp_PN'],
      5: ['i_id'], 6: ['i_qr'], 7: ['punti_vamp','punti_vaen','punti_vase'], 8: ['punti_vsi','punti_vse'],
      11: [`offer_${this.line}`,`production_${this.line}`,`sales_number_${this.line}`,`magazzino_${this.line}`]} as Record<number,string[]>)[this.chartId] ?? [];
  }

  refresh(): void {
    if (this.loading) return;
    this.loading = true; this.error = '';
    const oldRanking = JSON.stringify(this.period?.ranking);
    this.api.debriefing().subscribe({
      next: data => {
        this.data = data;
        this.teamIds = data.teams.map(t => t.id);
        if (this.periodNumber == null || !data.periods.some(p => p.number === this.periodNumber)) this.periodNumber = data.latestResultPeriod;
        if (!data.teams.some(t => t.id === this.focusTeam)) this.focusTeam = data.teams[0]?.id ?? '';
        if (!data.teams.some(t => t.id === this.detailTeam)) this.detailTeam = data.teams[0]?.id ?? '';
        if (oldRanking !== JSON.stringify(this.period?.ranking)) this.revealed = 1;
        this.loading = false; this.rebuild();
      },
      error: (e: HttpErrorResponse) => {
        this.loading = false;
        this.error = e.status === 401 ? 'Your teacher link has expired. Reopen the session from Caronte.'
          : e.status === 403 ? 'This link does not permit access to the teacher session.'
          : e.status === 404 ? 'The teacher session could not be found.' : 'Session data could not be loaded. Please retry.';
      }
    });
  }
  choosePeriod(): void { this.revealed = 1; this.rebuild(); }
  navigate(index: number): void { if (index < 0 || index >= slides.length) return; this.slide = index; this.menuOpen = false; this.rebuild(); }
  @HostListener('document:keydown', ['$event'])
  key(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || target?.closest('input,select,textarea,button,summary,[contenteditable]')) return;
    if (event.key === 'ArrowRight') { event.preventDefault(); this.navigate(this.slide + 1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.navigate(this.slide - 1); }
  }
  teamPeriod(period: SebgPeriod | undefined, id: string): SebgTeamPeriod | undefined { return period?.teams.find(t => t.teamId === id); }
  value(period: SebgPeriod | undefined, id: string, key: string): number | null {
    const value = this.teamPeriod(period, id)?.values[key];
    return value == null || !Number.isFinite(value) ? null : value;
  }
  format(value: unknown): string {
    if (value == null) return 'N/A';
    if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString('en-GB', {maximumFractionDigits: 3}) : 'N/A';
    if (typeof value === 'boolean') return value ? 'Selected' : 'Not selected';
    return String(value);
  }
  private readonly productDecisionLabels: Record<string, string> = {
    'price_Prod1-traditional': 'Traditional product price (per unit)',
    'price_Prod2-sustainable': 'Sustainable product price (per unit)',
    'n_Prod1-traditional': 'Traditional product production quantity (units)',
    'n_Prod2-sustainable': 'Sustainable product production quantity (units)'
  };
  decisionLabel(choice: SebgChoice): string { return this.productDecisionLabels[choice.key] ?? choice.label; }
  decisionDisplay(choice: SebgChoice, previous = false): string {
    const value = previous ? choice.previous : choice.current;
    if (this.productDecisionLabels[choice.key]) {
      return typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('en-GB', {maximumFractionDigits: 0}) : 'N/A';
    }
    return (previous ? choice.previousDisplay : choice.currentDisplay) ?? this.format(value);
  }
  private sum(period: SebgPeriod, id: string, entries: [string, number][]): number | null {
    const values = entries.map(([key, weight]) => { const v = this.value(period,id,key); return v == null ? null : v * weight; });
    if (values.some(v => v == null)) return null;
    const sum = values.reduce<number>((a,v) => a + v!, 0);
    return Number.isFinite(sum) ? sum : null;
  }
  private trend(key: string, unit: string, benchmark?: string): DebriefChart {
    const series: DebriefSeries[] = this.teams.map(t => ({label: t.name, teamId: t.id, values: this.history.map(p => this.value(p,t.id,key))}));
    if (benchmark) series.push({label: 'Market average', benchmark: true, values: this.history.map(p => p.benchmarks[benchmark] ?? null)});
    return {type: 'line', labels: this.history.map(p => `Decision ${p.decisionPeriod}`), series, xTitle: 'Decision', yTitle: unit};
  }
  private bars(keys: string[], labels: string[], unit: string, stacked = false): DebriefChart {
    return {type: 'bar', decision: this.period?.decisionPeriod, labels: this.teams.map(t => t.name),
      series: keys.map((key,i) => ({label: labels[i], styleIndex: i, values: this.teams.map(t => this.value(this.period,t.id,key))})),
      xTitle: 'Team', yTitle: unit, stacked};
  }
  rebuild(): void {
    const p = this.period;
    this.chart = undefined;
    this.conditionalPrompts = [];
    if (!p)
      return;
    switch (this.chartId) {
      case 13:
        this.buildPositioningChart();
        break;
      case 1:
        this.chart = this.trend(this.scoreMetric, this.scoreMetric === 'score' ? 'Ranking score (points)' : 'Decision score (points)');
        break;
      case 2:
        this.buildContributionChart(p);
        break;
      case 3:
        this.chart = this.trend('i_roi', 'ROI (%)', 'avg_i_roi');
        break;
      case 4:
        this.chart = this.trend('i_roe', 'ROE (%)', 'avg_i_roe');
        break;
      case 5:
        this.chart = this.trend('i_id', 'Debt / equity × 100 (game scale)', 'avg_i_id');
        break;
      case 6:
        this.chart = this.trend('i_qr', 'Quick ratio × 100 (game scale)', 'avg_i_qr');
        break;
      case 7:
        this.chart = this.bars(['norm_vamp', 'norm_vaen', 'norm_vase'], ['Raw materials', 'Energy', 'Waste / emissions'], 'Normalized model score');
        break;
      case 8:
        this.chart = this.bars(['norm_vsi', 'norm_vse'], ['Internal social', 'External social'], 'Normalized model score');
        break;
      case 9:
        this.buildSalesChart(p);
        break;
      case 10:
        this.chart = this.trend(`price_${this.line}`, 'Price (game currency / unit)', this.line === 't' ? 'avg_price_pt' : 'avg_price_ps');
        break;
      case 11:
        this.buildInventoryChart();
        break;
      case 12:
        this.buildDecisionChangesChart();
        break;
    }
    this.buildDiscussionPrompts(p);
  }
  private buildDiscussionPrompts(p: SebgPeriod): void {
    if (this.chartId === 2)
      this.contributionPrompts(p);
    const previous = this.data?.periods.find(h => h.number === p.number - 1);
    for (const t of this.teams) {
      if (this.chartId === 9) {
        const now = this.value(p, t.id, 'sales_number_s'), before = this.value(previous, t.id, 'sales_number_s');
        if (now != null && before != null && now > before)
          this.conditionalPrompts.push(`${t.name}: Sustainable sales increased compared with the previous decision. Which decisions might help explain this change?`);
      }
      if (this.chartId === 11) {
        const now = this.value(p, t.id, `magazzino_${this.line}`), before = this.value(previous, t.id, `magazzino_${this.line}`);
        if (now != null && before != null && now > before)
          this.conditionalPrompts.push(`${t.name}: Inventory increased compared with the previous decision. How did production compare with sales?`);
      }
      if (this.chartId === 12 && this.teamPeriod(p, t.id)?.changedCount === 0)
        this.conditionalPrompts.push(`${t.name}: Recorded recurring decisions are unchanged from the previous decision. Was maintaining the same choices part of your strategy?`);
    }
  }
  private positionPoint(period: SebgPeriod, teamId: string, selectedDecision: number): PositionPoint | null {
    const economic = this.sum(period, teamId, scoreDimensions[0][1]);
    const environmental = this.sum(period, teamId, scoreDimensions[1][1]);
    const social = this.sum(period, teamId, scoreDimensions[2][1]);
    if (economic == null || environmental == null || social == null || !Number.isFinite(environmental + social)) {
      return null;
    }
    const age = selectedDecision - period.decisionPeriod;
    return {
      x: environmental + social,
      y: economic,
      environmental,
      social,
      decision: period.decisionPeriod,
      radius: age === 0 ? 12 : age === 1 ? 7 : 3,
      historical: age > 1
    };
  }

  private buildPositioningChart(): void {
    const history = this.history;
    const selectedDecision = this.period!.decisionPeriod;
    this.chart = {
      type: 'scatter',
      labels: [],
      xTitle: 'Environmental + Social contribution (points)',
      yTitle: 'Economic contribution (points)',
      series: this.teams.map(team => ({
        label: team.name,
        teamId: team.id,
        values: [],
        points: history.flatMap((period, index) => {
          const point = this.positionPoint(period, team.id, selectedDecision);
          const gap = index > 0 && period.decisionPeriod !== history[index - 1].decisionPeriod + 1;
          return gap ? [null, point] : [point];
        })
      }))
    };
  }

  private contributionPrompts(p: SebgPeriod): void {
    for (const t of this.teams) {
      const economic = this.sum(p, t.id, scoreDimensions[0][1]), environmental = this.sum(p, t.id, scoreDimensions[1][1]);
      const invalidScore = this.availabilityIssues.some(issue => issue.team === t.name);
      if (!invalidScore && economic != null && environmental != null && economic > environmental)
        this.conditionalPrompts.push(`${t.name}: The economic contribution to this decision's score is larger than the environmental contribution. How do the scoring weights affect this comparison?`);
    }
  }
  private buildContributionChart(p: SebgPeriod): void {
    const dimensions = scoreDimensions;
    this.chart = { type: 'bar', decision: p.decisionPeriod, labels: this.teams.map(t => t.name), stacked: true,
      series: dimensions.map(([label, entries], i) => ({ label, styleIndex: i, values: this.teams.map(t => this.sum(p, t.id, entries)) })),
      xTitle: 'Team', yTitle: 'Weighted contribution to decision score (points)' };
  }
  private buildInventoryChart(): void {
    const team = this.teams.find(t => t.id === this.focusTeam) ?? this.teams[0];
    if (!team)
      return;
    this.focusTeam = team.id;
    this.chart = { type: 'line', companyName: team.name, labels: this.history.map(h => `Decision ${h.decisionPeriod}`),
      series: ['production_', 'sales_number_', 'magazzino_'].map((key, i) => ({ label: ['Production (persisted offer − opening inventory)', 'Sales', 'Closing inventory'][i], styleIndex: i, pointStyle: (['circle', 'rect', 'rectRot'] as const)[i],
        dash: [[], [8, 5], [2, 4]][i],
        values: this.history.map(h => this.value(h, team.id, key + this.line)) })), xTitle: 'Decision', yTitle: 'Units' };
  }
  private buildSalesChart(p: SebgPeriod): void {
    if (this.salesView === 'trend')
      this.chart = this.trend(`sales_number_${this.line}`, 'Units sold', `avg_sales_number_${this.line}`);
    else {
      this.chart = this.bars(['sales_number_t', 'sales_number_s'], ['Traditional', 'Sustainable'], 'Units sold', true);
      for (const line of ['t', 's'])
        this.chart.series.push({ label: line === 't' ? 'Traditional market average' : 'Sustainable market average', benchmark: true,
          type: 'line', dash: line === 't' ? [8, 5] : [2, 4],
          values: this.teams.map(() => p.benchmarks[`avg_sales_number_${line}`] ?? null) });
    }
  }
  private buildDecisionChangesChart(): void {
    this.chart = { type: 'bar', labels: this.history.map(h => `Decision ${h.decisionPeriod}`),
      series: this.teams.map(t => ({ label: t.name, teamId: t.id, values: this.history.map(h => this.teamPeriod(h, t.id)?.changedCount ?? null) })),
      integerTicks: true, xTitle: 'Decision', yTitle: 'Changed recurring choices' };
  }
}
